import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  ScrollArea,
  zodResolver,
} from "@devhop/ui";
import { XIcon } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { formatTime } from "@/shared/lib/date-time";
import { formatDateLocal } from "@/shared/lib/date-time-performance";
import type {
  RehearsalSessionDTO,
  UpdateRehearsalSessionDTO,
} from "../../domain/contracts";
import { countNewRehearsalSessions } from "../../domain/services/rehearsal-sessions-ui";
import type { CreateRehearsalSessionsBulkPayload } from "../api/queries";
import { RehearsalStudioBookingFields } from "./RehearsalStudioBookingFields";
import {
  buildCreateDefaultValues,
  buildStudioBookingPayload,
  type CreateFormValues,
  createRehearsalFormSchema,
  type EditFormValues,
  editRehearsalFormSchema,
} from "./rehearsal-session-form-types";
import {
  RehearsalStudioConflictProvider,
  useRehearsalStudioConflict,
  useRehearsalStudioConflictMonitor,
} from "./rehearsal-studio-conflict";

export type EventPerformerOption = { id: string; name: string };

type RehearsalSessionDialogProps = {
  open: boolean;
  mode: "create" | "edit";
  initial?: RehearsalSessionDTO | null;
  defaultDate?: string;
  eventPerformers: EventPerformerOption[];
  existingSessions: RehearsalSessionDTO[];
  defaultRoomId?: string | null;
  defaultRoomName?: string | null;
  canBookStudio?: boolean;
  submitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitCreate: (values: CreateRehearsalSessionsBulkPayload) => Promise<void>;
  onSubmitEdit: (values: UpdateRehearsalSessionDTO) => Promise<void>;
  onDelete?: () => Promise<void>;
};

function buildEditValues(
  initial?: RehearsalSessionDTO | null,
  defaultDate?: string,
): EditFormValues {
  return {
    performerId: initial?.performerId ?? "",
    scheduledDate: initial?.scheduledDate ?? defaultDate ?? "",
    location: initial?.location ?? "",
    status: initial?.status ?? "planned",
    notes: initial?.notes ?? "",
  };
}

function PerformerCheckboxList({
  performers,
  disabled,
}: {
  performers: EventPerformerOption[];
  disabled?: boolean;
}) {
  const { Controller, useFormContext } = RHF;
  const { control } = useFormContext<CreateFormValues>();

  if (performers.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        ຍັງບໍ່ມີນັກສະແດງໃນທີມ — ເພີ່ມນັກສະແດງກ່ອນ
      </p>
    );
  }

  return (
    <Controller
      control={control}
      name="performerIds"
      render={({ field }) => (
        <ScrollArea className="h-52 rounded-lg border bg-muted/20">
          <div className="space-y-2 p-3">
            {performers.map((p) => {
              const checked = field.value.includes(p.id);
              const inputId = `rehearsal-performer-${p.id}`;
              return (
                <div key={p.id} className="flex items-center gap-2.5 text-sm">
                  <Checkbox
                    id={inputId}
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(value) => {
                      if (value) {
                        field.onChange([...field.value, p.id]);
                      } else {
                        field.onChange(field.value.filter((id) => id !== p.id));
                      }
                    }}
                  />
                  <label
                    htmlFor={inputId}
                    className="flex-1 cursor-pointer truncate"
                  >
                    {p.name}
                  </label>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    />
  );
}

function RehearsalDatesPicker({
  disabled,
  conflictDates,
}: {
  disabled?: boolean;
  conflictDates?: Set<string>;
}) {
  const { useFormContext } = RHF;
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreateFormValues>();
  const dates = watch("scheduledDates") ?? [];
  const [draftDate, setDraftDate] = useState("");

  const addDate = () => {
    if (!draftDate || dates.includes(draftDate)) return;
    setValue("scheduledDates", [...dates, draftDate].sort(), {
      shouldValidate: true,
    });
    setDraftDate("");
  };

  const removeDate = (date: string) => {
    setValue(
      "scheduledDates",
      dates.filter((d) => d !== date),
      { shouldValidate: true },
    );
  };

  return (
    <div className="space-y-2 sm:col-span-2">
      <p className="font-medium text-sm">
        ວັນທີຊ້ອມ <span className="text-destructive">*</span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={draftDate}
          disabled={disabled}
          onChange={(e) => setDraftDate(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addDate();
            }
          }}
          className="flex h-9 min-w-[10.5rem] flex-1 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!draftDate || disabled}
          onClick={addDate}
        >
          ເພີ່ມວັນ
        </Button>
      </div>
      {dates.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {dates.map((date) => {
            const hasConflict = conflictDates?.has(date);
            return (
            <span
              key={date}
              className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-sm ${
                hasConflict
                  ? "border-destructive/50 bg-destructive/10 text-destructive"
                  : "bg-muted/30"
              }`}
            >
              {formatDateLocal(date)}
              {hasConflict ? (
                <span className="text-xs">(ຂັດແຍ້ງ)</span>
              ) : null}
              <button
                type="button"
                className="rounded-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                disabled={disabled}
                aria-label={`ລຶບ ${formatDateLocal(date)}`}
                onClick={() => removeDate(date)}
              >
                <XIcon className="size-3.5" />
              </button>
            </span>
            );
          })}
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">
          ເລືອກວັນແລ້ວກົດ «ເພີ່ມວັນ» — ເລືອກໄດ້ຫຼາຍວັນ
        </p>
      )}
      {errors.scheduledDates?.message ? (
        <p className="text-destructive text-sm">
          {errors.scheduledDates.message}
        </p>
      ) : null}
    </div>
  );
}

function CreateDialogFooter({
  submitting,
  existingSessions,
  onCancel,
}: {
  submitting?: boolean;
  existingSessions: RehearsalSessionDTO[];
  onCancel: () => void;
}) {
  const { useFormContext } = RHF;
  const { watch } = useFormContext<CreateFormValues>();
  const performerIds = watch("performerIds") ?? [];
  const scheduledDates = watch("scheduledDates") ?? [];
  const plannedCount = countNewRehearsalSessions(
    existingSessions,
    performerIds,
    scheduledDates,
  );
  const { submitBlocked, isChecking } = useRehearsalStudioConflict();
  const submitLabel = plannedCount > 0 ? `ເພີ່ມ (${plannedCount})` : "ເພີ່ມ";

  return (
    <DialogActions
      submitting={submitting}
      submitDisabled={submitBlocked}
      onCancel={onCancel}
      submitLabel={isChecking ? "ກຳລັງກວດສອບ..." : submitLabel}
    />
  );
}

export function RehearsalSessionDialog({
  open,
  mode,
  initial,
  defaultDate,
  eventPerformers,
  existingSessions,
  defaultRoomId,
  defaultRoomName,
  canBookStudio = false,
  submitting,
  onOpenChange,
  onSubmitCreate,
  onSubmitEdit,
  onDelete,
}: RehearsalSessionDialogProps) {
  const editMethods = RHF.useForm<EditFormValues>({
    resolver: zodResolver(editRehearsalFormSchema),
    defaultValues: buildEditValues(initial, defaultDate),
  });

  const createMethods = RHF.useForm<CreateFormValues>({
    resolver: zodResolver(createRehearsalFormSchema),
    defaultValues: buildCreateDefaultValues(
      eventPerformers,
      defaultDate,
      defaultRoomId,
    ),
  });

  useEffect(() => {
    if (!open) return;
    if (mode === "edit") {
      editMethods.reset(buildEditValues(initial, defaultDate));
    } else {
      createMethods.reset(
        buildCreateDefaultValues(eventPerformers, defaultDate, defaultRoomId),
      );
    }
  }, [
    open,
    mode,
    initial,
    defaultDate,
    defaultRoomId,
    eventPerformers,
    editMethods,
    createMethods,
  ]);

  const selectAllPerformers = () => {
    createMethods.setValue(
      "performerIds",
      eventPerformers.map((p) => p.id),
      { shouldValidate: true },
    );
  };

  const clearPerformers = () => {
    createMethods.setValue("performerIds", [], { shouldValidate: true });
  };

  const bookStudio = createMethods.watch("bookStudio");
  const studioRoomId = createMethods.watch("studioRoomId");
  const createScheduledDates = createMethods.watch("scheduledDates") ?? [];
  const studioStartTime = createMethods.watch("studioStartTime");
  const studioSlotMinutes = createMethods.watch("studioSlotMinutes");

  const studioConflictMonitor = useRehearsalStudioConflictMonitor(
    canBookStudio,
    {
      bookStudio,
      studioRoomId,
      scheduledDates: createScheduledDates,
      studioStartTime,
      studioSlotMinutes,
    },
    open && mode === "create",
  );

  const studioConflictDates = useMemo(
    () => new Set(studioConflictMonitor.conflictDetails.map((d) => d.date)),
    [studioConflictMonitor.conflictDetails],
  );

  const title = mode === "create" ? "ເພີ່ມວັນຊ້ອມ" : "ແກ້ໄຂວັນຊ້ອມ";
  const description =
    mode === "create"
      ? "ເລືອກຫຼາຍວັນ ແລະ ນັກສະແດງ — ສ້າງຫຼາຍລາຍການພ້ອມກັນ"
      : "ແກ້ໄຂລາຍລະອຽດວັນຊ້ອມຂອງນັກສະແດງຄົນນີ້";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-4xl flex max-h-[min(90vh,800px)] w-full flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {mode === "edit" ? (
          <FormRoot<EditFormValues>
            methods={editMethods}
            onSubmit={async (vals) => {
              await onSubmitEdit({
                performerId: vals.performerId,
                scheduledDate: vals.scheduledDate,
                location: vals.location || null,
                status: vals.status,
                notes: vals.notes || null,
              });
            }}
          >
            <ScrollArea className="max-h-[calc(min(90vh,800px)-10rem)] overflow-auto">
              <div className="space-y-4 px-6 py-4">
                <RehearsalSessionFields
                  mode="edit"
                  performerName={initial?.performerName ?? initial?.performerId}
                  roomBookingLabel={
                    initial?.roomName && initial.bookingStartTime
                      ? `${initial.roomName} ${formatTime(initial.bookingStartTime)}–${formatTime(initial.bookingEndTime ?? "")}`
                      : undefined
                  }
                  roomBookingId={initial?.roomBookingId}
                />
              </div>
            </ScrollArea>
            <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:justify-between">
              {onDelete ? (
                <Button
                  type="button"
                  variant="outline"
                  className="text-destructive"
                  disabled={submitting}
                  onClick={() => void onDelete()}
                >
                  ລຶບ
                </Button>
              ) : (
                <span />
              )}
              <DialogActions
                submitting={submitting}
                onCancel={() => onOpenChange(false)}
              />
            </DialogFooter>
          </FormRoot>
        ) : (
          <FormRoot<CreateFormValues>
            methods={createMethods}
            onSubmit={async (vals) => {
              const plannedCount = countNewRehearsalSessions(
                existingSessions,
                vals.performerIds,
                vals.scheduledDates,
              );
              if (plannedCount === 0) {
                createMethods.setError("scheduledDates", {
                  message:
                    "ທຸກຄູ່ (ວັນ × ນັກສະແດງ) ມີວັນຊ້ອມແລ້ວ — ກະລຸນາເລືອກວັນ ຫຼື ນັກສະແດງໃໝ່",
                });
                return;
              }
              const studioConflict =
                await studioConflictMonitor.runConflictCheck({
                  bookStudio: vals.bookStudio,
                  studioRoomId: vals.studioRoomId,
                  studioStartTime: vals.studioStartTime,
                  studioSlotMinutes: vals.studioSlotMinutes,
                  scheduledDates: vals.scheduledDates,
                });
              if (!studioConflict.ok) {
                createMethods.setError("studioRoomId", {
                  message: studioConflict.message,
                });
                return;
              }
              await onSubmitCreate({
                performerIds: vals.performerIds,
                scheduledDates: vals.scheduledDates,
                location: vals.location || null,
                status: vals.status,
                notes: vals.notes || null,
                studioBooking: buildStudioBookingPayload(vals),
              });
            }}
          >
            <RehearsalStudioConflictProvider value={studioConflictMonitor}>
              <ScrollArea className="max-h-[calc(min(90vh,800px)-10rem)] overflow-auto">
                <div className="px-6 py-4">
                  <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                    <section className="space-y-3">
                      <FormSectionTitle>ວັນ ແລະ ລາຍລະອຽດ</FormSectionTitle>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <RehearsalDatesPicker
                          disabled={submitting}
                          conflictDates={studioConflictDates}
                        />
                        <FormSelect
                          name="status"
                          label="ສະຖານະ"
                          options={REHEARSAL_STATUS_OPTIONS}
                        />
                      </div>
                      <FormInput
                        name="location"
                        label="ສະຖານທີ່"
                        placeholder="ທາງເລືອກ — ຈອງ Studio ຈະເຕີມອັດຕະໂນມັດ"
                      />
                      <FormTextarea name="notes" label="ໝາຍເຫດ" rows={3} />
                    </section>

                    <section className="flex min-h-0 flex-col gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <FormSectionTitle required>ນັກສະແດງ</FormSectionTitle>
                        {eventPerformers.length > 0 && (
                          <div className="flex shrink-0 gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={submitting}
                              onClick={selectAllPerformers}
                            >
                              ທັງໝົດ
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={submitting}
                              onClick={clearPerformers}
                            >
                              ລ້າງ
                            </Button>
                          </div>
                        )}
                      </div>
                      <PerformerCheckboxList
                        performers={eventPerformers}
                        disabled={submitting}
                      />
                    </section>
                  </div>

                  <section className="mt-6 space-y-3 border-t pt-6">
                    <FormSectionTitle>ຫ້ອງ Studio</FormSectionTitle>
                    <RehearsalStudioBookingFields
                      canBookStudio={canBookStudio}
                      defaultRoomId={defaultRoomId}
                      defaultRoomName={defaultRoomName}
                      disabled={submitting}
                    />
                  </section>
                </div>
              </ScrollArea>
              <DialogFooter className="shrink-0 border-t px-6 py-4">
                <CreateDialogFooter
                  submitting={submitting}
                  existingSessions={existingSessions}
                  onCancel={() => onOpenChange(false)}
                />
              </DialogFooter>
            </RehearsalStudioConflictProvider>
          </FormRoot>
        )}
      </DialogContent>
    </Dialog>
  );
}

const REHEARSAL_STATUS_OPTIONS = [
  { value: "planned", label: "ວາງແຜນ" },
  { value: "completed", label: "ສຳເລັດ (ນັບ payout)" },
  { value: "cancelled", label: "ຍົກເລີກ" },
];

function FormSectionTitle({
  children,
  required,
}: {
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <h3 className="font-medium text-sm tracking-tight">
      {children}
      {required ? <span className="text-destructive"> *</span> : null}
    </h3>
  );
}

function RehearsalSessionFields({
  mode,
  performerName,
  roomBookingLabel,
  roomBookingId,
}: {
  mode: "create" | "edit";
  performerName?: string;
  roomBookingLabel?: string;
  roomBookingId?: string | null;
}) {
  return (
    <>
      {mode === "edit" && performerName ? (
        <div>
          <p className="mb-1 font-medium text-sm">ນັກສະແດງ</p>
          <p className="text-sm">{performerName}</p>
        </div>
      ) : null}
      {mode === "edit" && roomBookingLabel ? (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
          <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
            ການຈອງຫ້ອງ Studio
          </p>
          <p>{roomBookingLabel}</p>
          {roomBookingId ? (
            <a
              href={`/app/room-bookings/${roomBookingId}`}
              className="mt-1 inline-block text-primary text-xs underline"
            >
              ເບິ່ງການຈອງຫ້ອງ
            </a>
          ) : null}
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <FormInput name="scheduledDate" label="ວັນທີຊ້ອມ" type="date" required />
        <FormSelect
          name="status"
          label="ສະຖານະ"
          options={REHEARSAL_STATUS_OPTIONS}
        />
      </div>
      <FormInput name="location" label="ສະຖານທີ່" placeholder="ທາງເລືອກ" />
      <FormTextarea name="notes" label="ໝາຍເຫດ" rows={3} />
    </>
  );
}

function DialogActions({
  submitting,
  submitDisabled,
  onCancel,
  submitLabel = "ບັນທຶກ",
}: {
  submitting?: boolean;
  submitDisabled?: boolean;
  onCancel: () => void;
  submitLabel?: string;
}) {
  return (
    <div className="flex w-full justify-end gap-2">
      <Button type="button" variant="outline" onClick={onCancel}>
        ຍົກເລີກ
      </Button>
      <Button type="submit" disabled={submitting || submitDisabled}>
        {submitLabel}
      </Button>
    </div>
  );
}
